/* Repository path: frontend/src/components/ItemReminder.tsx */

import { useState } from "react";
import type { ItemReminderStatus, PlanItem } from "../types";
import { itemReminderStatusIfValid } from "../api/estimate";
import { formatDate, formatReminderWhen, formatTimingWindow } from "../utils/format";
import { Button } from "./Button";
import { StatusChip } from "./StatusChip";

/**
 * The per-item reminder card line (`ItemReminderLine`) and detail section
 * (`ItemReminderSection`) — the two W5-02 surfaces that render
 * `PlanItem.reminder_status`. Both render only from a status that passed
 * `itemReminderStatusIfValid`; a malformed or absent status degrades to
 * "nothing shown for this item", never an error, exactly as `TimingLine`
 * and `RecordActual` already treat their own missing data.
 *
 * Neither component computes a date itself. Every date on screen here
 * comes from the server's already-derived `reminder_status` — the same
 * function that decides what the worker sends — so the screen can never
 * show a state the worker would disagree with. See
 * docs/architecture/features/W5-02.md §4 and
 * docs/design/features/W5-02.md.
 */

export type SetItemReminder = (itemCode: string, enabled: boolean) => Promise<void>;

function whenSentence(status: ItemReminderStatus): string | null {
  const parts = status.planned.map((entry) => formatReminderWhen(entry.on, entry.precision));
  if (parts.length === 0) return null;
  if (parts.length === 1) return `We’ll email you ${parts[0]}.`;
  if (parts.length === 2) return `We’ll email you ${parts[0]}, and again ${parts[1]}.`;
  return `We’ll email you ${parts[0]}, ${parts[1]} and ${parts[2]}.`;
}

function basisSentence(status: ItemReminderStatus): string | null {
  const about = status.about;
  if (!about || !status.kind) return null;
  const window = about.window_start
    ? formatTimingWindow(about.window_start, about.window_end, about.precision)
    : "";
  const day = about.window_start ? formatDate(about.window_start) : "";

  switch (status.kind) {
    case "user_supplied_deadline": {
      let sentence = `Based on the date you entered: ${day}.`;
      if (status.superseded.includes("fixed_deadline")) {
        sentence += " We’re using it instead of the official date.";
      } else if (status.superseded.includes("expected_window")) {
        sentence += " We’re using it instead of the usual timing.";
      }
      return sentence;
    }
    case "fixed_deadline":
      return `Based on the official date: ${day}.`;
    case "expected_window":
      return `Based on when this usually happens: ${window}. There’s no exact date for it yet, so the reminder is a heads-up, not a deadline.`;
    case "action":
      return about.precision === "day"
        ? `Based on the official timing for this step: by ${day}.`
        : `Based on the official timing for this step: usually ${window}.`;
    case "review":
      return "Because we still need one answer from you to estimate this.";
    default:
      return null;
  }
}

function endedSentence(status: ItemReminderStatus): string {
  if (status.ended_because === "settled") {
    return "No more reminders for this — you’ve marked it as done.";
  }
  return "This date has gone by, so there are no more reminders for it.";
}

/**
 * The card's one quiet line — placed directly after `TimingLine`, before
 * the actual line, exactly as the architecture and design handoffs place
 * it. Rendered only for `active` (with something still planned) and
 * `item_off`; every other state (including a `null` status) renders
 * nothing at all, so an ineligible item never grows an apologetic line.
 * Rendered only when the caller supplies `onSetReminder` (a saved plan) —
 * a preview never shows this line, matching `TimingLine`'s own
 * `editable`-gated pattern.
 */
export function ItemReminderLine({
  item,
  onSetReminder,
  online = true,
}: {
  item: PlanItem;
  onSetReminder?: SetItemReminder;
  online?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = itemReminderStatusIfValid(item.reminder_status);

  if (!status || !onSetReminder) return null;
  if (status.state === "active" && status.planned.length === 0) return null;
  if (status.state !== "active" && status.state !== "item_off") return null;

  const isOff = status.state === "item_off";
  const label = isOff ? "Turn on" : "Turn off";
  const errorId = `item-reminder-line-error-${item.code}`;

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSetReminder(item.code, isOff);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn’t change this reminder. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <p className="estimate-card__reminder">
      <StatusChip tone="neutral">Email reminder</StatusChip>{" "}
      {isOff ? "Off" : formatReminderWhen(status.planned[0].on, status.planned[0].precision)}{" "}
      <Button
        type="button"
        variant="quiet"
        busy={busy}
        disabled={!online}
        onClick={() => void toggle()}
        aria-describedby={error ? errorId : undefined}
      >
        {busy ? "Saving…" : label}{" "}
        <span className="sr-only">the reminder for {item.english_name}</span>
      </Button>
      {!online && <span className="field-hint"> Connect to the internet to change this reminder.</span>}
      {error && (
        <span className="field-error" id={errorId} role="alert">
          {" "}{error}
        </span>
      )}
    </p>
  );
}

/**
 * The detail view's `Email reminders` section content — `PlanItemDetail`
 * owns the surrounding `<section>` and heading, exactly as it does for
 * `recordActual`; this component renders only the states table's content.
 * `null` is a valid, expected return for an ineligible item — `PlanItemDetail`
 * omits the whole section when this renders nothing, via the same
 * `reminder ? <section>…</section> : null` pattern `recordActual` already
 * uses.
 */
export function ItemReminderSection({
  item,
  onSetReminder,
  online = true,
}: {
  item: PlanItem;
  onSetReminder?: SetItemReminder;
  online?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = itemReminderStatusIfValid(item.reminder_status);

  if (!status) return null;

  const errorId = `item-reminder-error-${item.code}`;

  const toggle = async (enabled: boolean) => {
    if (!onSetReminder) return;
    setBusy(true);
    setError(null);
    try {
      await onSetReminder(item.code, enabled);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn’t change this reminder. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const basis = basisSentence(status);
  const lastSent = status.last_sent ? `Last reminder sent ${formatReminderWhen(status.last_sent.on, status.last_sent.precision)}.` : null;

  return (
    <>
      {status.state === "active" && (
        <>
          <p>{whenSentence(status) ?? "We’ve sent the reminders we planned for this."}</p>
          {basis && <p>{basis}</p>}
          {lastSent && <p>{lastSent}</p>}
          {status.can_change && onSetReminder && (
            <>
              <Button type="button" variant="secondary" busy={busy} disabled={!online} onClick={() => void toggle(false)} aria-describedby={error ? errorId : `item-reminder-hint-${item.code}`}>
                {busy ? "Saving…" : "Turn off reminders for this item"}
              </Button>
              <p className="field-hint" id={`item-reminder-hint-${item.code}`}>
                You’ll still get your other reminders for this plan.
              </p>
            </>
          )}
        </>
      )}

      {status.state === "item_off" && (
        <>
          <p>Reminders for this item are off.</p>
          {basis && <p>{basis}</p>}
          {lastSent && <p>{lastSent}</p>}
          <p className="field-hint" id={`item-reminder-hint-${item.code}`}>
            You’ll still get your other reminders for this plan.
          </p>
          {onSetReminder && (
            <Button type="button" variant="secondary" busy={busy} disabled={!online} onClick={() => void toggle(true)} aria-describedby={error ? errorId : `item-reminder-hint-${item.code}`}>
              {busy ? "Saving…" : "Turn reminders back on for this item"}
            </Button>
          )}
        </>
      )}

      {status.state === "plan_off" && (
        <p>
          Reminders are off for this whole plan. You can turn them back on from “Manage this saved
          plan” on your plan page.
        </p>
      )}

      {status.state === "no_longer_needed" && <p>{endedSentence(status)}</p>}

      {!online && (status.state === "active" || status.state === "item_off") && (
        <p className="field-hint" role="status">
          Connect to the internet to change this reminder.
        </p>
      )}
      {error && (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </>
  );
}
